import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/apiClient";
import { fileSrc } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import TopBar from "../components/TopBar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "../components/ui/alert-dialog";
import { Plus, ArrowRight, Bell, Trash2, Pencil, Image as ImageIcon, ChevronDown, ChevronRight, Search } from "lucide-react";
import { toast } from "sonner";
import PhaseStepper, { PHASE_COLORS } from "../components/PhaseStepper";
import ImportProjectsDialog from "../components/ImportProjectsDialog";
import Footer from "../components/Footer";

const STATUS_COLORS = PHASE_COLORS;

function StatusBadge({ status }) {
  const c = STATUS_COLORS[status] || "#A68A6B";
  return (
    <span className="inline-flex items-center gap-2 text-xs uppercase tracking-wider" style={{ color: c }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: c }} />
      {status}
    </span>
  );
}

function ProjectCard({ p, navigate, isDesigner, onDelete, onEdit, onCover }) {
  return (
    <div data-testid={`project-card-${p.id}`}
      onClick={() => navigate(`/projects/${p.id}`)}
      className="group relative text-left bg-white overflow-hidden hover:bg-[#F9F8F6] transition-colors cursor-pointer">
      {p.cover_image && (
        <div className="relative h-40 bg-[#F2EFEB] overflow-hidden" data-testid={`project-cover-${p.id}`}>
          <img src={fileSrc(p.cover_image)} alt={p.name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
          {isDesigner && (
            <label onClick={(e) => e.stopPropagation()} data-testid={`cover-change-label-${p.id}`}
              className="absolute bottom-2 right-2 bg-white/90 hover:bg-white text-[#2B2622] text-[10px] uppercase tracking-wider px-2.5 py-1.5 cursor-pointer inline-flex items-center gap-1.5 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity">
              <ImageIcon size={12} strokeWidth={1.75} /> Change
              <input type="file" accept="image/*" className="hidden" data-testid={`cover-input-${p.id}`}
                onChange={(e) => { onCover(p, e.target.files?.[0]); e.target.value = ""; }} />
            </label>
          )}
        </div>
      )}
      <div className="p-7">
      <div className="flex items-start justify-between">
        <StatusBadge status={p.status} />
        <div className="flex items-center gap-2">
          {(p.unread_notes > 0 || p.new_approvals > 0) && (
            <span data-testid={`project-notif-${p.id}`}
              className="inline-flex items-center gap-1 bg-[#A68A6B] text-white text-[10px] uppercase tracking-wider px-2 py-1">
              <Bell size={12} strokeWidth={2} />
              {(p.unread_notes || 0) + (p.new_approvals || 0)} new
            </span>
          )}
          {isDesigner && !p.cover_image && (
            <label onClick={(e) => e.stopPropagation()} data-testid={`cover-add-label-${p.id}`}
              title="Add photo"
              className="text-[#C9BBAE] hover:text-[#A68A6B] transition-colors opacity-0 group-hover:opacity-100 cursor-pointer">
              <ImageIcon size={16} strokeWidth={1.5} />
              <input type="file" accept="image/*" className="hidden" data-testid={`cover-input-${p.id}`}
                onChange={(e) => { onCover(p, e.target.files?.[0]); e.target.value = ""; }} />
            </label>
          )}
          {isDesigner && (
            <button data-testid={`edit-project-${p.id}`}
              onClick={(e) => { e.stopPropagation(); onEdit(p); }}
              title="Rename project"
              className="text-[#C9BBAE] hover:text-[#A68A6B] transition-colors opacity-0 group-hover:opacity-100">
              <Pencil size={16} strokeWidth={1.5} />
            </button>
          )}
          {isDesigner && (
            <button data-testid={`delete-project-${p.id}`}
              onClick={(e) => { e.stopPropagation(); onDelete(p); }}
              title="Delete project"
              className="text-[#C9BBAE] hover:text-[#9E4747] transition-colors opacity-0 group-hover:opacity-100">
              <Trash2 size={16} strokeWidth={1.5} />
            </button>
          )}
        </div>
      </div>
      <h3 className="font-serif text-2xl text-[#2B2622] mt-3 mb-2 group-hover:text-[#A68A6B] transition-colors">{p.name}</h3>
      <p className="text-sm text-[#5C5651] line-clamp-2 min-h-[40px]">{p.description || "No description provided."}</p>
      <div className="mt-6 h-px bg-[#EBE5E0]" />
      <div className="flex items-center justify-between mt-4">
        <div>
          <p className="text-[11px] uppercase tracking-wider text-[#A68A6B]">Client</p>
          <p className="text-sm text-[#2B2622]">{p.client_name || p.client_email || "Unassigned"}</p>
        </div>
        <ArrowRight size={18} strokeWidth={1.5} className="text-[#A68A6B] group-hover:translate-x-1 transition-transform" />
      </div>
      <div className="mt-4">
        <div className="flex justify-between text-[11px] text-[#5C5651] mb-2">
          <span className="uppercase tracking-wider text-[#A68A6B]">Phase</span>
          <span>{p.status}</span>
        </div>
        <PhaseStepper status={p.status} />
      </div>
      {isDesigner && p.checklist_total > 0 && (
        <div className="mt-4" data-testid={`checklist-progress-${p.id}`}>
          <div className="flex justify-between text-[11px] text-[#5C5651] mb-2">
            <span className="uppercase tracking-wider text-[#A68A6B]">Checklists</span>
            <span>{p.checklist_done}/{p.checklist_total} done</span>
          </div>
          <div className="h-1 bg-[#F2EFEB]">
            <div className="h-full bg-[#A68A6B] transition-all"
              style={{ width: `${Math.round((p.checklist_done / p.checklist_total) * 100)}%` }} />
          </div>
        </div>
      )}
      </div>
    </div>
  );
}


export default function Dashboard() {
  const { user, previewClient } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", client_email: "", budget_total: "" });
  const [busy, setBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editName, setEditName] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [completedOpen, setCompletedOpen] = useState(() => {
    try { return localStorage.getItem("wr_completed_open") !== "false"; }
    catch { return true; }
  });
  const [completedSearch, setCompletedSearch] = useState("");

  useEffect(() => {
    try { localStorage.setItem("wr_completed_open", String(completedOpen)); } catch { /* ignore */ }
  }, [completedOpen]);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/projects/${deleteTarget.id}`);
      setProjects((prev) => prev.filter((p) => p.id !== deleteTarget.id));
      toast.success("Project permanently deleted");
      setDeleteTarget(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setDeleting(false);
    }
  };

  const openEdit = (p) => { setEditTarget(p); setEditName(p.name || ""); };

  const uploadCover = async (project, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/projects/${project.id}/cover`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setProjects((prev) => prev.map((p) => (p.id === project.id ? { ...p, cover_image: data.cover_image } : p)));
      toast.success("Project photo updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editTarget || !editName.trim()) return;
    setSavingEdit(true);
    try {
      const { data } = await api.put(`/projects/${editTarget.id}`, { name: editName.trim() });
      setProjects((prev) => prev.map((p) => (p.id === editTarget.id ? { ...p, name: data.name } : p)));
      toast.success("Project renamed");
      setEditTarget(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSavingEdit(false);
    }
  };

  const load = () => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/projects", {
        name: form.name,
        description: form.description,
        client_email: form.client_email || null,
        budget_total: parseFloat(form.budget_total) || 0,
      });
      toast.success("Project created");
      setOpen(false);
      setForm({ name: "", description: "", client_email: "", budget_total: "" });
      setLoading(true);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const canCreate = user?.role === "designer" || user?.role === "admin";
  const isDesigner = user?.role === "designer" && !previewClient;
  const activeProjects = projects.filter((p) => p.status !== "Complete");
  const completedProjects = projects.filter((p) => p.status === "Complete");
  const completedFiltered = completedProjects.filter((p) =>
    (p.name || "").toLowerCase().includes(completedSearch.trim().toLowerCase()));

  const Section = ({ title, list }) => (
    <section className="mb-12" data-testid={`section-${title.toLowerCase().split(" ")[0]}`}>
      <div className="flex items-baseline gap-3 mb-4">
        <h2 className="font-serif text-2xl text-[#2B2622]">{title}</h2>
        <span className="text-[11px] uppercase tracking-wider text-[#A68A6B]">{list.length}</span>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#EBE5E0] border border-[#EBE5E0]">
        {list.map((p) => (
          <ProjectCard key={p.id} p={p} navigate={navigate} isDesigner={isDesigner} onDelete={setDeleteTarget} onEdit={openEdit} onCover={uploadCover} />
        ))}
      </div>
    </section>
  );

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="dashboard-page">
      <TopBar />
      <main className="max-w-7xl mx-auto px-6 sm:px-8 py-12">
        <div className="flex items-end justify-between mb-10 border-b border-[#EBE5E0] pb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-[#A68A6B] mb-2">Your Workspace</p>
            <h1 className="font-serif text-4xl sm:text-5xl text-[#2B2622]">Projects</h1>
            <p className="text-[#5C5651] mt-2 text-sm">
              {projects.length} {projects.length === 1 ? "project" : "projects"} in your studio
            </p>
          </div>
          {canCreate && (
            <div className="flex items-center gap-3">
              {isDesigner && <ImportProjectsDialog onImported={load} />}
              <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="new-project-btn"
                  className="rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs py-6 px-6">
                  <Plus size={16} className="mr-2" strokeWidth={1.5} /> New Project
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-none border-[#EBE5E0] bg-white">
                <DialogHeader>
                  <DialogTitle className="font-serif text-2xl text-[#2B2622]">Create project</DialogTitle>
                </DialogHeader>
                <form onSubmit={create} className="space-y-4 mt-2">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#5C5651]">Project name</Label>
                    <Input data-testid="project-name-input" required value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="mt-1 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#5C5651]">Description</Label>
                    <Textarea data-testid="project-desc-input" value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      className="mt-1 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-[#5C5651]">Client email</Label>
                      <Input data-testid="project-client-input" type="email" value={form.client_email}
                        onChange={(e) => setForm({ ...form, client_email: e.target.value })}
                        placeholder="optional"
                        className="mt-1 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-[#5C5651]">Budget ($)</Label>
                      <Input data-testid="project-budget-input" type="number" value={form.budget_total}
                        onChange={(e) => setForm({ ...form, budget_total: e.target.value })}
                        className="mt-1 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
                    </div>
                  </div>
                  <Button data-testid="create-project-submit" type="submit" disabled={busy}
                    className="w-full rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs py-6">
                    {busy ? "Creating…" : "Create Project"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
            </div>
          )}
        </div>

        {loading ? (
          <p className="text-[#5C5651]">Loading…</p>
        ) : projects.length === 0 ? (
          <div className="border border-dashed border-[#D9CBBF] bg-white p-16 text-center">
            <p className="font-serif text-2xl text-[#2B2622] mb-2">No projects yet</p>
            <p className="text-[#5C5651] text-sm">
              {canCreate ? "Create your first project to get started." : "Your designer hasn't shared a project with you yet."}
            </p>
          </div>
        ) : (
          <>
            {activeProjects.length > 0 && <Section title="Active Projects" list={activeProjects} />}
            {completedProjects.length > 0 && (
              <section className="mb-12" data-testid="section-completed">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-3 mb-4">
                  <button data-testid="toggle-completed-section" onClick={() => setCompletedOpen((o) => !o)}
                    className="flex items-center gap-2 text-[#2B2622] hover:text-[#A68A6B] transition-colors">
                    {completedOpen ? <ChevronDown size={20} strokeWidth={1.5} /> : <ChevronRight size={20} strokeWidth={1.5} />}
                    <h2 className="font-serif text-2xl">Completed Projects</h2>
                    <span className="text-[11px] uppercase tracking-wider text-[#A68A6B]">{completedProjects.length}</span>
                  </button>
                  {completedOpen && (
                    <div className="relative ml-auto w-full sm:w-72">
                      <Search size={15} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A68A6B]" />
                      <Input data-testid="completed-search-input" value={completedSearch}
                        onChange={(e) => setCompletedSearch(e.target.value)}
                        placeholder="Search completed by name…"
                        className="pl-9 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B] text-sm" />
                    </div>
                  )}
                </div>
                {completedOpen && (
                  completedFiltered.length === 0 ? (
                    <p className="text-sm text-[#5C5651] py-6" data-testid="completed-no-results">
                      No completed projects match “{completedSearch}”.
                    </p>
                  ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#EBE5E0] border border-[#EBE5E0]">
                      {completedFiltered.map((p) => (
                        <ProjectCard key={p.id} p={p} navigate={navigate} isDesigner={isDesigner} onDelete={setDeleteTarget} onEdit={openEdit} onCover={uploadCover} />
                      ))}
                    </div>
                  )
                )}
              </section>
            )}
          </>
        )}
      </main>

      <Footer />

      <Dialog open={!!editTarget} onOpenChange={(o) => { if (!o) setEditTarget(null); }}>
        <DialogContent className="rounded-none border-[#EBE5E0] bg-white" data-testid="edit-project-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#2B2622]">Rename project</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveEdit} className="space-y-4 mt-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5C5651]">Project name</Label>
              <Input data-testid="edit-project-name-input" required autoFocus value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="mt-1 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
            </div>
            <Button data-testid="save-project-name-btn" type="submit" disabled={savingEdit || !editName.trim()}
              className="w-full rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs py-6">
              {savingEdit ? "Saving…" : "Save Changes"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent className="rounded-none border-[#EBE5E0] bg-white" data-testid="delete-project-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl text-[#2B2622]">Permanently delete project?</AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-[#5C5651]">
              This will permanently delete <span className="font-medium text-[#2B2622]">{deleteTarget?.name}</span> and all of its
              budget, approvals, mood boards, notes, documents and files. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-project" className="rounded-none border-[#EBE5E0] uppercase tracking-wider text-xs">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-project" onClick={(e) => { e.preventDefault(); confirmDelete(); }} disabled={deleting}
              className="rounded-none bg-[#9E4747] hover:bg-[#853a3a] text-white uppercase tracking-wider text-xs">
              {deleting ? "Deleting…" : "Permanently Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
